import { message } from 'antd'
import { ProviderHelperClient } from '~/plugins/data-connector/helpers/provider.helper.client'
import { MimeTypes } from '../../common'

type Options = {
  mimetypes?: MimeTypes[]
  isMultiple?: boolean
}

let currentPicker: google.picker.Picker | null = null

const MAX_FILE_SIZE = 25 * 1024 * 1024

export const GooglePickerPopup = {
  open: openGooglePickerPopup,
}

async function openGooglePickerPopup(
  accessToken: string,
  options?: Options,
): Promise<GooglePickerPopupResponse> {
  const projectNumber = Number(
    ProviderHelperClient.getEnvVariable('google', 'PROJECT_NUMBER') ?? '0',
  )
  const isMultiple = options?.isMultiple ?? true
  const mimetypes = options?.mimetypes ?? [
    MimeTypes.FOLDER,
    MimeTypes.GOOGLE_DOC,
    MimeTypes.GOOGLE_SHEET,
    MimeTypes.DOCX,
    MimeTypes.XLSX,
    MimeTypes.CSV,
    MimeTypes.PDF,
    MimeTypes.MSG,
    MimeTypes.PNG,
    MimeTypes.JPEG,
  ]

  return new Promise<GooglePickerPopupResponse>((resolve, reject) => {
    loadGooglePicker(() => {
      // Build the picker UI
      let pickerBuilder = new google.picker.PickerBuilder()
        .setOAuthToken(accessToken)
        .setAppId(projectNumber)
        .addView(
          new google.picker.DocsView()
            .setSelectFolderEnabled(true)
            .setMimeTypes(mimetypes.join(',')),
        )

      if (isMultiple) {
        pickerBuilder = pickerBuilder.enableFeature(
          google.picker.Feature.MULTISELECT_ENABLED,
        )
      }

      currentPicker = pickerBuilder
        .setCallback(data => {
          if (data.action === google.picker.Action.PICKED) {
            const items = data.docs.reduce(
              (files, doc) => {
                if (doc.sizeBytes > MAX_FILE_SIZE) {
                  message.error(
                    `File "${doc.name}" exceeds maximum file size of 25MB`,
                  )
                  return files
                }

                files.push({
                  id: doc.id,
                  name: doc.name,
                  mimeType: doc.mimeType,
                })

                return files
              },
              [] as GooglePickerPopupResponse['items'],
            )

            resolve({ items })
          } else if (data.action === google.picker.Action.CANCEL) {
            resolve({
              items: [],
            })
          }
        })
        .build()
      currentPicker.setVisible(true)
    })
  })
}

function loadGooglePicker(callback: () => void) {
  if (window.gapi?.picker) {
    // If already loaded, call the callback directly
    callback()
    return
  }

  const script = document.createElement('script')
  script.src = 'https://apis.google.com/js/api.js'
  script.onload = () => gapi.load('picker', callback)
  document.body.appendChild(script)
}

// Types
type GooglePickerPopupResponse = {
  items: {
    id: string
    name: string
    mimeType: string
  }[]
}
